import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { withRouter, Switch as RouterSwitch } from 'react-router-dom'
import { Button, Modal, Space, Table, Empty } from 'antd'
import PageLoader from '../../../components/Layouts/PageLoader'
import PrivateRoute from '../../../hocs/PrivateRoute'
import PaginationContainer from '../../../components/Forms/Pagination'
import {
  getRequest,
  patchRequest,
  deleteRequest,
  formatUrl,
  dutyKbUrl,
  successNotification,
  errorNotification
} from '../../../config'
import { columns } from './tableColumns'
import KnowledgeForm from './KnowledgeForm'

const KnowledgeBaseList = () => {
  const [loading, setLoading] = useState(false)
  const [entries, setEntries] = useState([])
  const [meta, setMeta] = useState({ previous: null, next: null, count: 0 })
  const [showFormModal, setShowFormModal] = useState(false)
  const [currentRecord, setCurrentRecord] = useState(null)
  const [togglingId, setTogglingId] = useState(null)
  const [deletingId, setDeletingId] = useState(null)
  const [modalKey, setModalKey] = useState(0)

  // `url` is a paginator next/previous link when navigating pages; otherwise we load page 1.
  const getEntries = async (url = null) => {
    const response = await getRequest(
      url ? formatUrl(url) : dutyKbUrl,
      'knowledge entries',
      setLoading
    )
    if (response.success) {
      setEntries(response.data.results || response.data)
      setMeta({ ...response.data })
    }
  }

  useEffect(() => {
    getEntries()
    // eslint-disable-next-line
  }, [])

  const openCreate = () => {
    setCurrentRecord(null)
    setModalKey(k => k + 1)
    setShowFormModal(true)
  }

  const openEdit = record => {
    setCurrentRecord(record)
    setModalKey(k => k + 1)
    setShowFormModal(true)
  }

  const handleDone = () => {
    setShowFormModal(false)
    getEntries()
  }

  const handleToggleActive = async record => {
    setTogglingId(record.id)
    const response = await patchRequest(
      `${dutyKbUrl}/${record.id}/`,
      { isActive: !record.isActive },
      'knowledge entry',
      () => {}
    )
    setTogglingId(null)
    if (response.success) {
      successNotification(record.isActive ? 'Entry deactivated' : 'Entry activated')
      getEntries()
    } else {
      errorNotification('Could not update the entry, retry.')
    }
  }

  const handleDelete = async record => {
    setDeletingId(record.id)
    const response = await deleteRequest(
      `${dutyKbUrl}/${record.id}/`,
      'knowledge entry',
      () => {}
    )
    setDeletingId(null)
    if (response.success) {
      successNotification('Entry deleted')
      getEntries()
    }
  }

  return (
    <PageLoader className="container" loading={loading}>
      <div className="card mb-5rem">
        <div className="card-body">
          <h3 style={{ marginBottom: 16 }}>
            Knowledge Base {meta && `(${meta.count})`}
          </h3>
          <div style={{ marginBottom: 16 }}>
            <Space>
              <Button type="primary" onClick={openCreate}>
                New Entry
              </Button>
              <Button onClick={() => getEntries()}>Refresh</Button>
            </Space>
          </div>

          <Table
            rowKey="id"
            dataSource={entries}
            columns={columns(
              openEdit,
              handleToggleActive,
              handleDelete,
              togglingId,
              deletingId
            )}
            scroll={{ x: 1050 }}
            pagination={false}
            locale={{
              emptyText: <Empty description="No knowledge entries yet" />
            }}
          />
          {entries.length ? (
            <PaginationContainer meta={meta} getData={url => getEntries(url)} />
          ) : null}
        </div>
      </div>

      <Modal
        title={currentRecord ? 'Edit Knowledge Entry' : 'New Knowledge Entry'}
        centered
        open={showFormModal}
        onCancel={() => setShowFormModal(false)}
        footer={null}
        width={700}
      >
        <KnowledgeForm
          key={modalKey}
          record={currentRecord}
          handleDone={handleDone}
        />
      </Modal>
    </PageLoader>
  )
}

const KnowledgeBaseRoutes = props => (
  <RouterSwitch>
    <PrivateRoute exact path={props.match.path} component={KnowledgeBaseList} />
  </RouterSwitch>
)

KnowledgeBaseRoutes.propTypes = {
  match: PropTypes.object
}

export default withRouter(KnowledgeBaseRoutes)
